<template>
  <v-dialog v-model="show" max-width="1200px" scrollable>
    <v-card v-if="artikl">
      <v-card-title class="py-2">
        <span class="subtitle-1 font-weight-bold">
          {{ artikl.itemID }}<span v-if="artikl.itemName"> - {{ artikl.itemName }}</span>
        </span>
        <span v-if="podnaslov" class="caption grey--text ml-3">{{ podnaslov }}</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="show = false"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-2" style="max-height: 70vh;">
        <v-row dense class="mb-1">
          <v-col cols="12">
            <span class="caption">
              Razdoblje {{ localDate(fromDate) }} - {{ localDate(toDate) }} ·
              stanje na kraju <b>{{ kol(stanje) }}</b> {{ artikl.itemUnit }}
              <span v-if="artikl.avgNC"> · prosj. NC <b>{{ novac(artikl.avgNC) }}</b></span>
            </span>
          </v-col>
        </v-row>
        <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>
        <v-data-table
          dense
          :headers="headers"
          :items="rows"
          :items-per-page="-1"
          hide-default-footer
          no-data-text="Nema promjena u razdoblju."
        >
          <template v-slot:[`item.documentDate`]="{ item }">{{ localDate(item.documentDate) }}</template>
          <template v-slot:[`item.documentName`]="{ item }">
            {{ docName(item.documentName) }} {{ item.documentId }}
          </template>
          <template v-slot:[`item.ulaz`]="{ item }">
            <span v-if="item.ulaz">{{ kol(item.ulaz) }}</span>
          </template>
          <template v-slot:[`item.izlaz`]="{ item }">
            <span v-if="item.izlaz">{{ kol(item.izlaz) }}</span>
          </template>
          <template v-slot:[`item.stanje`]="{ item }">
            <span :class="item.stanje < 0 ? 'red--text' : ''">{{ kol(item.stanje) }}</span>
          </template>
          <template v-slot:[`item.nabCijena`]="{ item }">{{ novac(item.nabCijena) }}</template>
          <template v-slot:[`item.prodCijena`]="{ item }">{{ novac(item.prodCijena) }}</template>
          <template v-slot:[`item.prodVr`]="{ item }">
            <span v-if="item.izlaz">{{ novac(item.prodVr) }}</span>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <v-snackbar v-model="snackbar" bottom :timeout="4000" color="red">{{ greska }}</v-snackbar>
  </v-dialog>
</template>

<script>
// Kartica artikla - svaka promjena stanja u razdoblju, najstarija prva, sa
// stanjem koje se vodi niz listu.
//
// Dijele je stanje skladista i pregled po dobavnim cijenama, pa se otvara
// preko ref-a umjesto propsa:
//
//   <WareHouseItemCard ref="kartica" />
//   this.$refs.kartica.open({itemID: '1234', itemName: 'Vijak', fromDate, toDate})
//
// Firma i poslovna godina dolaze iz storea, datumi i skladiste su neobavezni -
// bez njih se uzima cijela poslovna godina.

import WareHouseStockService from '@/services/WareHouseStockService'
import documentLocalName from '@/snippets/documentLocalName'
import dayjs from 'dayjs'

const num = (v) => {
  const n = parseFloat(v)
  return isNaN(n) ? 0 : n
}

export default {
  name: 'warehouseitemcard',
  data () {
    return {
      show: false,
      loading: false,
      artikl: null,
      rows: [],
      stanje: 0,
      fromDate: null,
      toDate: null,
      wareHouseId: null,
      snackbar: false,
      greska: ''
    }
  },
  computed: {
    headers () {
      return [
        {text: 'Datum', value: 'documentDate', width: '100px'},
        {text: 'Dokument', value: 'documentName', width: '190px'},
        {text: 'Partner', value: 'partnerName'},
        {text: 'Ulaz', value: 'ulaz', align: 'end', width: '90px'},
        {text: 'Izlaz', value: 'izlaz', align: 'end', width: '90px'},
        {text: 'Stanje', value: 'stanje', align: 'end', width: '90px'},
        {text: 'Nab. cijena', value: 'nabCijena', align: 'end', width: '100px'},
        {text: 'Prod. cijena', value: 'prodCijena', align: 'end', width: '100px'},
        {text: 'Prod. vr.', value: 'prodVr', align: 'end', width: '100px'}
      ]
    },
    podnaslov () {
      if (!this.artikl) { return '' }
      return [this.artikl.itemUnit, this.artikl.GroupName || this.artikl.itemGroup]
        .filter(x => x).join(' · ')
    }
  },
  methods: {
    kol (v) {
      const n = num(v)
      const dec = Math.abs(n - Math.round(n)) < 0.0001 ? 0 : 2
      return n.toFixed(dec).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    novac (v) {
      return num(v).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    localDate (d) {
      return d ? dayjs(d).format('DD.MM.YYYY') : ''
    },
    docName (n) {
      return n ? documentLocalName(n) : ''
    },

    /**
     * @param {Object} p
     *   itemID      obavezno
     *   itemName, itemUnit, itemGroup, GroupName, avgNC   samo za zaglavlje
     *   fromDate, toDate   neobavezno, inace cijela poslovna godina
     *   WareHouseId        neobavezno
     */
    async open (p) {
      const itemID = p && p.itemID ? String(p.itemID).trim() : ''
      if (!itemID) {
        this.greska = 'Artikl nema šifru, kartica se ne može otvoriti.'
        this.snackbar = true
        return
      }
      const godina = this.$store.state.businessYear
      this.artikl = Object.assign({}, p)
      this.fromDate = p.fromDate || (godina + '-01-01')
      this.toDate = p.toDate || (godina + '-12-31')
      this.wareHouseId = p.WareHouseId || null
      this.rows = []
      this.stanje = 0
      this.show = true
      this.loading = true
      try {
        const {data} = await WareHouseStockService.card({
          CompanyId: this.$store.state.companyid,
          BusinessYear: godina,
          itemID: itemID,
          WareHouseId: this.wareHouseId || undefined,
          fromDate: this.fromDate,
          toDate: this.toDate
        })
        this.rows = data && data.rows ? data.rows : []
        this.stanje = data && data.meta ? data.meta.zavrsnoStanje : 0
      } catch (err) {
        this.greska = 'Greška pri dohvatu kartice: ' + (err && err.message ? err.message : err)
        this.snackbar = true
      }
      this.loading = false
    }
  }
}
</script>
